import { useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCurrentPerson } from './useCurrentPerson';
import db from '@/db/db';
import { Client, isClientSlug } from '@/db/types/client';

/**
 * Resolves the `:id` route param to a client — by numeric `id` or by `label` (slug).
 *
 * Lookup strategy:
 * - If the param starts with "ami-" → `db.clients.where('label').equals(slug).first()`
 * - Otherwise (numeric) → `db.clients.get(num)`
 *
 * Verifies `organization_id` matches the current person's organization.
 *
 * Returns:
 * - `client`: the raw Client object (undefined while loading, null if not found / wrong org)
 * - `clientId`: the numeric client id (null while loading or if not found)
 * - `clientSlug`: the client's label / slug (undefined while loading or not found)
 * - `loading`: true while the query is still resolving
 */
export function useClientFromParam() {
  const { id } = useParams<{ id: string }>();
  const { organizationId } = useCurrentPerson();

  const isSlug = isClientSlug(id);

  const client = useLiveQuery(async (): Promise<Client | null | undefined> => {
    if (!id || !organizationId) return undefined;

    let clientData: Client | undefined;

    if (isSlug) {
      clientData = await db.clients.where('label').equals(id).first();
    } else {
      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) return null;
      clientData = await db.clients.get(numericId);
    }

    if (!clientData) return null;

    // Verify the client belongs to the current organization
    if (clientData.organization_id !== organizationId) {
      return null;
    }

    return clientData;
  }, [id, organizationId]);

  const loading = client === undefined || !organizationId;

  return {
    client: client as Client | null | undefined,
    clientId: client?.id ?? null,
    clientSlug: client?.label,
    loading,
  };
}
